import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useRef, useEffect } from 'react';
import { useFloating, autoUpdate, offset, flip, size, shift, limitShift, hide, arrow, useTransitionStyles, FloatingArrow } from '@floating-ui/react';
import { Box } from '../Box/Box.web.js';
import { popover } from './styles.js';
import { Portal } from '../utils/components/Portal/Portal.web.js';
import { ThemeProvider } from 'styled-components';
import { IconButton } from '../IconButton/IconButton.web.js';
import { IconCross } from '../icons/ui_control/IconCross.web.js';
import { timings } from '../constants/timings.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { spacing } from '../constants/spacing.js';

var _excluded = ["anchor", "children", "backgroundColor", "opened", "onClose", "fallbackPlacements", "anchorPos", "boundary", "popperOffset", "popperPadding", "shadowStyle", "showArrow", "disableShift", "disableTether", "disablePortal", "variant", "textColor", "closeButtonAriaLabel", "iconButtonProps", "hideCloseButton", "style"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Popover = function Popover(props) {
  var anchor = props.anchor,
    children = props.children,
    backgroundColorProp = props.backgroundColor,
    opened = props.opened,
    onClose = props.onClose,
    _props$fallbackPlacem = props.fallbackPlacements,
    fallbackPlacements = _props$fallbackPlacem === undefined ? ['top', 'top-end', 'top-start', 'bottom', 'right', 'left'] : _props$fallbackPlacem,
    _props$anchorPos = props.anchorPos,
    anchorPos = _props$anchorPos === undefined ? 'top' : _props$anchorPos,
    boundary = props.boundary,
    _props$popperOffset = props.popperOffset,
    popperOffset = _props$popperOffset === undefined ? 9 : _props$popperOffset,
    _props$popperPadding = props.popperPadding,
    popperPadding = _props$popperPadding === undefined ? 16 : _props$popperPadding,
    shadowStyle = props.shadowStyle,
    _props$showArrow = props.showArrow,
    showArrow = _props$showArrow === undefined ? true : _props$showArrow,
    _props$disableShift = props.disableShift,
    disableShift = _props$disableShift === undefined ? false : _props$disableShift,
    _props$disableTether = props.disableTether,
    disableTether = _props$disableTether === undefined ? false : _props$disableTether,
    disablePortal = props.disablePortal,
    variantProp = props.variant,
    textColor = props.textColor,
    _props$closeButtonAri = props.closeButtonAriaLabel,
    closeButtonAriaLabel = _props$closeButtonAri === undefined ? 'Close' : _props$closeButtonAri,
    iconButtonProps = props.iconButtonProps,
    _props$hideCloseButto = props.hideCloseButton,
    hideCloseButton = _props$hideCloseButto === undefined ? true : _props$hideCloseButto,
    style = props.style,
    rest = _objectWithoutProperties(props, _excluded);
  var arrowRef = useRef(null);
  var _useFloating = useFloating({
      open: opened,
      placement: anchorPos,
      whileElementsMounted: autoUpdate,
      onOpenChange: onClose,
      middleware: [offset(popperOffset), flip({
        fallbackPlacements: fallbackPlacements,
        padding: popperPadding,
        boundary: boundary
      }), size({
        apply: function apply(_ref) {
          var elements = _ref.elements;
          Object.assign(elements.floating.style, {
            maxWidth: "calc(100vw - ".concat(popperPadding * 2, "px)")
          });
        }
      }), !disableShift ? shift({
        padding: popperPadding,
        mainAxis: true,
        crossAxis: true,
        boundary: boundary,
        limiter: !disableTether ? limitShift({
          offset: popperOffset
        }) : undefined
      }) : undefined, hide({
        strategy: 'referenceHidden',
        padding: popperPadding
      }), arrow({
        element: arrowRef
      })]
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    context = _useFloating.context;
  var _useTransitionStyles = useTransitionStyles(context, {
      duration: timings.fastest,
      open: {
        opacity: 1
      },
      close: {
        opacity: 0
      }
    }),
    isMounted = _useTransitionStyles.isMounted,
    transitionStyles = _useTransitionStyles.styles;
  useEffect(function () {
    if (anchor) {
      refs.setReference(anchor);
    } else {
      refs.setReference(null);
    }
  }, [anchor, refs]);
  React.useEffect(function () {
    // istanbul ignore if - just a sanity check
    if (!onClose) return;
    var mouseListener = function mouseListener(event) {
      // close if opened and anchor is clicked
      if (opened && anchor !== null && anchor !== undefined && anchor.contains(event.target)) {
        // after react 17 upgrade handleOpen is called after the onClose, making popover flicker closed/open
        setTimeout(function () {
          onClose();
        });
        return;
      }
      if (!(refs.floating.current && refs.floating.current.contains(event.target))) {
        onClose();
      }
    };
    var keyListener = function keyListener(event) {
      if (refs.floating.current && event.key === 'Escape') {
        onClose();
      }
    };
    if (opened) {
      document.addEventListener('mouseup', mouseListener, {
        capture: true
      });
      document.addEventListener('touchend', mouseListener, {
        capture: true
      });
      document.addEventListener('keydown', keyListener, {
        capture: true
      });
    } else {
      document.removeEventListener('mouseup', mouseListener, {
        capture: true
      });
      document.removeEventListener('touchend', mouseListener, {
        capture: true
      });
      document.removeEventListener('keydown', keyListener, {
        capture: true
      });
    }
    return function () {
      document.removeEventListener('mouseup', mouseListener, {
        capture: true
      });
      document.removeEventListener('touchend', mouseListener, {
        capture: true
      });
      document.removeEventListener('keydown', keyListener, {
        capture: true
      });
    };
  }, [opened, refs.floating, onClose, anchor]);
  var theme = useTheme();
  var popoverTheme = theme.popover.web;
  var backgroundColor = backgroundColorProp || popoverTheme.background;
  var variant = variantProp || theme.variant;
  var isCondensed = variant === 'condensed';
  if (!opened) {
    return null;
  }
  var popoverContent = isMounted ? /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(popover, _extends({
    ref: refs.setFloating,
    boxSizing: "border-box",
    style: _objectSpread(_objectSpread(_objectSpread({}, style), floatingStyles), transitionStyles),
    backgroundColor: backgroundColor,
    "data-testid": "elemental-Popover",
    padding: isCondensed ? spacing.xxs : spacing.xs,
    zIndex: props.zIndex ? props.zIndex : 1000,
    borderRadius: popoverTheme.borderRadius,
    $popoverShadowStyle: shadowStyle || popoverTheme.container.shadowStyle,
    textColor: textColor
  }, rest), showArrow && /*#__PURE__*/React.createElement(FloatingArrow, {
    ref: arrowRef,
    fill: backgroundColor,
    context: context
  }), /*#__PURE__*/React.createElement(Box, {
    flex: "1",
    display: "flex",
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, children), !hideCloseButton && /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: IconCross,
    color: backgroundColor,
    textColor: textColor,
    onClick: onClose,
    tabIndex: undefined,
    "data-testid": "elemental-Popover-close",
    "aria-label": closeButtonAriaLabel
    // Allow slight overflow into surrounding padding so the icon is in
    // line with content
    ,
    m: "-".concat(isCondensed ? spacing.xxxs : spacing.xxs)
    // Don't however let content overlap with the IconButton
    ,
    ml: spacing.xxxs
  }, iconButtonProps))))) : null;
  return disablePortal ? popoverContent : /*#__PURE__*/React.createElement(Portal, {
    container: document.body
  }, popoverContent);
};

export { Popover };
